import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { EnvelopeSimple, PaperPlaneRight, Trash, ArrowBendUpLeft, Plus, X, File, Eye, Paperclip, DownloadSimple, FileDoc, FilePdf } from "@phosphor-icons/react";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function FileIcon({ type }) {
  if (type?.includes("pdf")) return <FilePdf size={16} className="text-red-500" />;
  if (type?.includes("word") || type?.includes("document")) return <FileDoc size={16} className="text-blue-500" />;
  return <File size={16} className="text-[#6B7280]" />;
}

export default function EmailsPage() {
  const { user } = useAuth();
  const [emails, setEmails] = useState([]);
  const [folder, setFolder] = useState("inbox");
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({ to: "", subject: "", body: "", related_type: "", related_id: "", is_draft: false });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const FOLDERS = [
    { key: "inbox", label: "Inbox", icon: EnvelopeSimple },
    { key: "sent", label: "Sent", icon: PaperPlaneRight },
    { key: "drafts", label: "Drafts", icon: File },
  ];

  useEffect(() => { loadEmails(); }, [folder]);

  const loadEmails = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/emails?folder=${folder}`);
      setEmails(data.items || []);
    } catch { toast.error("Failed to load emails"); }
    finally { setLoading(false); }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} is too large (max 10MB)`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments(prev => [...prev, { id: data.id, filename: data.original_filename, content_type: data.content_type, size: data.size }]);
      } catch (err) { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (idx) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const handleSend = async (isDraft = false) => {
    if (!isDraft && (!form.to.trim() || !form.subject.trim())) { toast.error("To and Subject are required"); return; }
    try {
      const payload = { ...form, is_draft: isDraft, attachments: attachments.map(a => ({ file_id: a.id, filename: a.filename, content_type: a.content_type, size: a.size })) };
      const { data } = await api.post("/emails", payload);
      if (!isDraft && data.delivery_status === "failed") {
        const err = data.delivery_error || "";
        if (err.includes("verify a domain") || err.includes("testing emails")) {
          toast.error("Email delivery failed — sending domain needs verification in Resend.", { duration: 8000 });
        } else {
          toast.error("Email saved but delivery failed: " + (err || "Unknown error"));
        }
      } else {
        toast.success(isDraft ? "Draft saved" : "Email sent");
      }
      setShowCompose(false);
      setForm({ to: "", subject: "", body: "", related_type: "", related_id: "", is_draft: false });
      setAttachments([]);
      loadEmails();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleReply = async () => {
    if (!form.body.trim()) { toast.error("Reply body is required"); return; }
    try {
      await api.post(`/emails/reply/${showDetail.id}`, form);
      toast.success("Reply sent");
      setShowDetail(null);
      setForm({ to: "", subject: "", body: "", related_type: "", related_id: "", is_draft: false });
      loadEmails();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to reply"); }
  };

  const handleDelete = async (emailId) => {
    try {
      await api.delete(`/emails/${emailId}`);
      toast.success("Email deleted");
      if (showDetail?.id === emailId) setShowDetail(null);
      loadEmails();
    } catch { toast.error("Failed to delete"); }
  };

  const openEmail = async (emailId) => {
    try {
      const { data } = await api.get(`/emails/${emailId}`);
      setShowDetail(data);
    } catch { toast.error("Failed to load email"); }
  };

  const downloadAttachment = async (att) => {
    try {
      const fid = att.file_id || att.id;
      if (!fid) { toast.error("No download available"); return; }
      const resp = await api.get(`/files/download/${fid}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "file";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="emails-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Email</h1>
          <p className="text-sm text-[#6B7280] mt-1">Send real emails to clients and candidates via Resend</p>
        </div>
        <Button onClick={() => { setShowCompose(true); setShowDetail(null); setAttachments([]); }} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="compose-email-btn">
          <Plus size={16} /> Compose
        </Button>
      </div>

      <div className="flex gap-4">
        <div className="flex md:flex-col gap-1 md:w-48 flex-shrink-0">
          {FOLDERS.map(f => (
            <button key={f.key} onClick={() => { setFolder(f.key); setShowDetail(null); }}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${folder === f.key ? "bg-[#0F172A] text-white" : "text-[#6B7280] hover:bg-[#F3F4F6]"}`}
              data-testid={`folder-${f.key}`}>
              <f.icon size={16} /> {f.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0">
          {showCompose ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="compose-form">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-base font-semibold text-[#0F172A]">New Email</h2>
                <button onClick={() => setShowCompose(false)}><X size={18} className="text-[#6B7280]" /></button>
              </div>
              <div className="space-y-3">
                <Input placeholder="To (email)" value={form.to} onChange={e => setForm({...form, to: e.target.value})} data-testid="email-to-input" />
                <Input placeholder="Subject" value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} data-testid="email-subject-input" />
                <textarea className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none min-h-[160px]" placeholder="Write your message..." value={form.body} onChange={e => setForm({...form, body: e.target.value})} data-testid="email-body-input" />

                {/* Attachments */}
                <div>
                  <input ref={fileRef} type="file" multiple accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleFileSelect} data-testid="email-file-input" />
                  <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1.5 text-xs" data-testid="email-attach-btn">
                    <Paperclip size={14} /> {uploading ? "Uploading..." : "Attach Files"}
                  </Button>
                  <span className="text-[10px] text-[#9CA3AF] ml-2">PDF, Word, images — max 10MB each</span>
                  {attachments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {attachments.map((a, i) => (
                        <div key={i} className="flex items-center gap-2 bg-[#F9FAFB] rounded-lg px-3 py-2 border border-[#E5E7EB]" data-testid={`attachment-${i}`}>
                          <FileIcon type={a.content_type} />
                          <span className="text-xs font-medium text-[#0F172A] truncate flex-1">{a.filename}</span>
                          <span className="text-[10px] text-[#9CA3AF]">{formatSize(a.size)}</span>
                          <button onClick={() => removeAttachment(i)} className="text-[#9CA3AF] hover:text-[#EF4444]"><X size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => handleSend(false)} className="bg-[#0F172A] hover:bg-[#1E293B] gap-2" data-testid="send-email-btn"><PaperPlaneRight size={16} /> Send</Button>
                  <Button variant="outline" onClick={() => handleSend(true)} data-testid="save-draft-btn">Save Draft</Button>
                </div>
              </div>
            </div>
          ) : showDetail ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-5" data-testid="email-detail">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-base font-semibold text-[#0F172A]">{showDetail.subject}</h2>
                  <p className="text-xs text-[#6B7280] mt-1">From: {showDetail.from_name} ({showDetail.from_email}) &middot; To: {showDetail.to}</p>
                  <p className="text-xs text-[#6B7280]">{showDetail.created_at ? new Date(showDetail.created_at).toLocaleString() : ""}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleDelete(showDetail.id)} className="text-red-400 hover:text-red-600"><Trash size={18} /></button>
                  <button onClick={() => setShowDetail(null)}><X size={18} className="text-[#6B7280]" /></button>
                </div>
              </div>
              <div className="text-sm text-[#374151] whitespace-pre-wrap mb-4 min-h-[120px] p-3 bg-[#F9FAFB] rounded-lg">{showDetail.body}</div>

              {/* Display attachments */}
              {showDetail.attachments?.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Attachments</p>
                  <div className="space-y-1">
                    {showDetail.attachments.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 bg-[#F9FAFB] rounded-lg px-3 py-2 border border-[#E5E7EB]" data-testid={`detail-attachment-${i}`}>
                        <FileIcon type={a.content_type} />
                        <span className="text-xs font-medium text-[#0F172A] truncate flex-1">{a.filename}</span>
                        <span className="text-[10px] text-[#9CA3AF]">{formatSize(a.size)}</span>
                        <button onClick={() => downloadAttachment(a)} className="text-[#1E3A8A] hover:text-[#0F172A]" data-testid={`download-attachment-${i}`}>
                          <DownloadSimple size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border-t border-[#E5E7EB] pt-4">
                <h3 className="text-sm font-semibold mb-2">Reply</h3>
                <textarea className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm resize-none min-h-[80px]" placeholder="Write your reply..." value={form.body} onChange={e => setForm({...form, body: e.target.value})} data-testid="reply-body-input" />
                <Button onClick={handleReply} size="sm" className="mt-2 bg-[#0F172A] gap-2" data-testid="reply-email-btn"><ArrowBendUpLeft size={14} /> Reply</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {loading ? (
                [1,2,3].map(i => <div key={i} className="h-16 skeleton-shimmer rounded-lg" />)
              ) : emails.length === 0 ? (
                <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
                  <EnvelopeSimple size={48} className="text-[#D1D5DB] mx-auto mb-3" />
                  <p className="text-[#6B7280]">No emails in {folder}</p>
                </div>
              ) : (
                emails.map(email => (
                  <button key={email.id} onClick={() => openEmail(email.id)} className={`w-full bg-white rounded-lg border border-[#E5E7EB] px-4 py-3 text-left hover:shadow-sm transition-shadow ${!email.is_read && folder === "inbox" ? "border-l-4 border-l-[#1E3A8A]" : ""}`} data-testid={`email-item-${email.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${!email.is_read && folder === "inbox" ? "font-semibold text-[#0F172A]" : "text-[#374151]"}`}>{email.subject || "(no subject)"}</p>
                          {email.attachments?.length > 0 && <Paperclip size={12} className="text-[#9CA3AF] flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-[#6B7280] truncate mt-0.5">
                          {folder === "inbox" ? `From: ${email.from_name || email.from_email}` : `To: ${email.to}`}
                          {email.is_inbound && <span className="ml-1 text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">Reply</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                        {email.delivery_status && folder === "sent" && (
                          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${email.delivery_status === "delivered" ? "bg-green-100 text-green-700" : email.delivery_status === "failed" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>
                            {email.delivery_status}
                          </span>
                        )}
                        <span className="text-[10px] text-[#6B7280]">{email.created_at ? new Date(email.created_at).toLocaleDateString() : ""}</span>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
